// Sparkline — animated SVG area chart for the capture-pulse card. Hand-rolled
// (no chart lib) so it stays crisp, tiny, and honest: one point per real
// PT-day bucket from loadCaptureSeries. The line draws in on mount and the
// latest point carries a glowing pulse dot.

import { motion } from 'framer-motion';

type SparklineProps = {
  /** Per-day values, oldest → newest (from CaptureSeries.counts). */
  values: number[];
  /** Matching day keys for the hover tooltips. */
  days?: string[];
  height?: number;
  tone?: 'forest' | 'sage';
};

const W = 600;
const PAD_Y = 10;

const TONE = {
  forest: { line: '#1F4437', fillFrom: 'rgba(45,125,95,0.22)', dot: '#2D7D5F' },
  sage: { line: '#5F7A56', fillFrom: 'rgba(143,174,126,0.25)', dot: '#8FAE7E' },
} as const;

export default function Sparkline({
  values,
  days,
  height = 64,
  tone = 'forest',
}: SparklineProps): JSX.Element {
  const { line, fillFrom, dot } = TONE[tone];
  const n = values.length;
  if (n < 2) {
    return (
      <div className="h-16 flex items-center text-xs text-muted-foreground">
        Not enough data yet
      </div>
    );
  }
  const max = Math.max(...values, 1);
  const px = (i: number): number => (i / (n - 1)) * W;
  const py = (v: number): number => height - PAD_Y - (v / max) * (height - PAD_Y * 2);
  const points = values.map((v, i) => [px(i), py(v)] as const);
  const linePath = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  const areaPath = `${linePath} L${W},${height} L0,${height} Z`;
  const [lastX, lastY] = points[n - 1];
  const gradId = `sparkline-fill-${tone}`;

  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      className="w-full"
      style={{ height }}
      role="img"
      aria-label={`Activity over the last ${n} days; today: ${values[n - 1]}`}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fillFrom} />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
      </defs>
      <motion.path
        d={areaPath}
        fill={`url(#${gradId})`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.4 }}
      />
      <motion.path
        d={linePath}
        fill="none"
        stroke={line}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.2, ease: 'easeInOut' }}
        style={{ filter: `drop-shadow(0 1px 4px ${fillFrom})` }}
      />
      {/* Invisible per-day hit targets for native tooltips. */}
      {days &&
        points.map(([x], i) => (
          <rect
            key={days[i]}
            x={x - W / (n - 1) / 2}
            y={0}
            width={W / (n - 1)}
            height={height}
            fill="transparent"
          >
            <title>{`${days[i]}: ${values[i]}`}</title>
          </rect>
        ))}
      <motion.circle
        cx={lastX}
        cy={lastY}
        r={4.5}
        fill={dot}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.35, ease: 'backOut' }}
        style={{ filter: `drop-shadow(0 0 6px ${dot})` }}
      />
    </svg>
  );
}
